import { createFeatureSelector, createSelector } from '@ngrx/store';
import moment from 'moment';
import { intersects } from 'radash';
import { GradeLesson } from '../../models/grade-lesson.school.model';
import {
  gradeLessonAdapter,
  GradeLessonState,
} from '../reducers/grade-lesson.reducer';
import { selectRouteParams } from './router.selector';

export const getGradeLessonState =
  createFeatureSelector<GradeLessonState>('gradeLessons');
export const getIds = (state: GradeLessonState) => state.ids;

export const {
  selectIds: getGradeLessonIds,
  selectEntities: getGradeLessonEntities,
  selectAll: getAllGradeLessons,
  selectTotal: getTotalGradeLessons,
} = gradeLessonAdapter.getSelectors(getGradeLessonState);

export const loading = createSelector(
  getGradeLessonState,
  (gradeLessonState: GradeLessonState) => gradeLessonState.loading,
);

export const inProgress = createSelector(
  getGradeLessonState,
  (gradeLessonState: GradeLessonState) => gradeLessonState.inProgress,
);

export const getGradeLessonsBySelectedGroup = (academicYear: number) =>
  createSelector(getAllGradeLessons, selectRouteParams, (allItems, { id }) => {
    if (allItems) {
      return allItems.filter((item) => {
        return item.groupId === id && item.academicYear === academicYear;
      });
    } else {
      return null;
    }
  });

export const getGradeLessonsCountForSelectedGroup = createSelector(
  getAllGradeLessons,
  selectRouteParams,
  (allItems, { id }) => {
    return allItems.filter((item) => {
      return item.groupId === id;
    }).length;
  },
);

export const getGradeLessonById = (lessonId: string) =>
  createSelector(getAllGradeLessons, selectRouteParams, (allItems, { id }) => {
    if (allItems) {
      return allItems.find((item) => {
        return item.groupId === id && item.id == lessonId;
      });
    } else {
      return null;
    }
  });

export const getGradeLessonsByGradeId = ({
  gradeId,
  groupId,
  academicYear,
  lessonStatus,
  publishBy,
}: {
  gradeId: string | null | undefined;
  groupId: string | null | undefined;
  academicYear: number | null | undefined;
  lessonStatus?: string[];
  publishBy?: boolean;
}) =>
  createSelector(getAllGradeLessons, (allItems) => {
    if (!gradeId || !academicYear) return [];
    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId &&
          item.gradeId === gradeId &&
          (academicYear == null || item.academicYear === academicYear) &&
          (publishBy == null ||
            (item.publishBy &&
              moment(item.publishBy.toDate()).isBefore(moment()))) &&
          (lessonStatus == undefined ||
            (item.lessonStatus !== undefined &&
              intersects([item.lessonStatus], lessonStatus)))
        );
      })
      .sort(sortDescendingByPublishDate);
  });

export const getGradeLessonsBySelectedGrade = (academicYear?: number) =>
  createSelector(
    getAllGradeLessons,
    selectRouteParams,
    (allItems, { id, gradeId }) => {
      return allItems
        .filter((item) => {
          return (
            item.groupId === id &&
            item.gradeId === gradeId &&
            item.academicYear === academicYear
          );
        })
        .sort(sortDescendingByPublishDate);
    },
  );

export const getGradeLessonIdsByGroup = (groupId: string) =>
  createSelector(getAllGradeLessons, (allItems) => {
    return allItems
      .filter((item) => item.groupId === groupId)
      .map((item) => item.id)
      .filter((item) => item != undefined);
  });

function sortDescendingByCreatedDate(a: GradeLesson, b: GradeLesson): number {
  return a.created! > b.created! ? -1 : a.created! < b.created! ? 1 : 0;
}

function sortDescendingByPublishDate(a: GradeLesson, b: GradeLesson): number {
  return a.publishBy! > b.publishBy! ? -1 : a.publishBy! < b.publishBy! ? 1 : 0;
}
